import { useEffect, type RefObject } from 'react';

const SELECTOR = '.rv, #headline, [data-step], #car, #final';

/**
 * Появление блоков при прокрутке (портировано из js/main.js, `IntersectionObserver` на
 * `.rv,#headline,[data-step],#car,#final`) — класс `.rv` в landing.css стартует с
 * `opacity:0;transform:translateY(40px)`, класс `.in` отключает это. Без наблюдателя
 * блоки остаются невидимыми: это не декоративная мелочь, а обязательное условие,
 * чтобы контент вообще показался.
 *
 * AICODE-NOTE: элементы, смонтированные ПОСЛЕ первого рендера (блок пересоздан, пришли данные),
 * подхватывает MutationObserver. Раньше был только снимок `querySelectorAll` при монтировании —
 * и поле лотереи с «Мои подарки», пересозданные после загрузки состояния участия, оставались
 * прозрачными навсегда. Не возвращать снимок без MutationObserver.
 *
 * AICODE-NOTE: React перезаписывает `className` элемента при каждом изменении его классов (карточка поля:
 * busy/open/ghost) и стирает поставленный здесь `in` — элемент снова становится прозрачным. Поэтому уже
 * показанные элементы запоминаются, и MutationObserver по атрибуту `class` возвращает им `in` до отрисовки.
 */
export function useReveal(rootRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const revealed = new WeakSet<Element>();
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('in');
            revealed.add(entry.target);
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.15 },
    );
    const watch = (el: Element) => {
      if (el.matches(SELECTOR) && !el.classList.contains('in')) io.observe(el);
      el.querySelectorAll(SELECTOR).forEach((t) => {
        if (!t.classList.contains('in')) io.observe(t);
      });
    };
    watch(root);
    const mo = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes') {
          const el = m.target as Element;
          if (revealed.has(el) && !el.classList.contains('in')) el.classList.add('in');
        } else {
          m.addedNodes.forEach((n) => n instanceof Element && watch(n));
        }
      }
    });
    mo.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    return () => {
      mo.disconnect();
      io.disconnect();
    };
  }, [rootRef]);
}
